/**
 * Organizations Module - TanStack Query Hooks
 *
 * This module provides all hooks for organization-related operations including:
 * - Organization data management
 * - Member management
 * - Credits/Quotes management
 * - API keys management
 * - Organization reports
 * - Invitations
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { GeneralOrganizationData } from "js/components/Organization/types";
import { PaginationTable } from "js/components/types";

import { CreditData } from "js/pages/Organization/types";

import {
  addMember,
  createOrganization,
  deleteMember,
  getAdvisorApiKeys,
  getCredits,
  getMembers,
  getOrganization,
  getOrganizationReports,
  getOrganizationTenantReports,
  inviteToOrganization,
  joinToOrganization,
  updateCredits,
  updateOrganization,
} from "../api/organizations";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get current organization data
 * @returns Organization data with loading and error states
 */
export const useGetOrganization = () => {
  return useQuery({
    queryKey: queryKeys.organization.data(),
    queryFn: getOrganization,
    staleTime: 5 * 60 * 1000, // 5 minutes - org data doesn't change frequently
  });
};

/**
 * Get organization members
 * @returns Members list with loading and error states
 */
export const useGetMembers = () => {
  return useQuery({
    queryKey: queryKeys.organization.members(),
    queryFn: getMembers,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get organization credits/quotes
 * @returns Credits data with loading and error states
 */
export const useGetOrganizationCredits = () => {
  return useQuery({
    queryKey: queryKeys.organization.credits(),
    queryFn: getCredits,
    staleTime: 1 * 60 * 1000, // 1 minute - credits change frequently
    refetchOnMount: "always",
  });
};

/**
 * Get organization advisor API keys
 * @returns API keys data with loading and error states
 */
export const useGetAdvisorApiKeys = () => {
  return useQuery({
    queryKey: queryKeys.organization.apiKeys(),
    queryFn: getAdvisorApiKeys,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get organization reports with pagination and filters
 * @param pagination - Pagination parameters
 * @param filter - Filter string
 * @returns Reports data with loading and error states
 */
export const useGetOrganizationReports = (
  pagination: PaginationTable,
  filter?: string,
) => {
  return useQuery({
    queryKey: queryKeys.organization.reports({ pagination, filter }),
    queryFn: () => getOrganizationReports(pagination, filter),
    staleTime: 30 * 1000, // 30 seconds - reports are dynamic
  });
};

/**
 * Get organization tenant reports with pagination and filters
 * @param pagination - Pagination parameters
 * @param filter - Filter string
 * @returns Tenant reports data with loading and error states
 */
export const useGetOrganizationTenantReports = (
  pagination: PaginationTable,
  filter?: string,
) => {
  return useQuery({
    queryKey: queryKeys.organization.tenantReports.list({ pagination, filter }),
    queryFn: () => getOrganizationTenantReports(pagination, filter),
    staleTime: 30 * 1000, // 30 seconds - reports are dynamic
  });
};

// ============================================================================
// MUTATION HOOKS (CREATE, UPDATE, DELETE)
// ============================================================================

/**
 * Create a new organization
 * Invalidates: organization data
 */
export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      toast.success("Organización creada exitosamente");
      // Invalidate organization data to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.data(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al crear la organización";
      toast.error(errorMessage);
    },
  });
};

/**
 * Update organization information
 * Invalidates: organization data, potentially members
 */
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: GeneralOrganizationData) => updateOrganization(values),
    onSuccess: () => {
      toast.success("Organización actualizada exitosamente");
      // Invalidate organization data and members (in case organization settings affect members)
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.data(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.members(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar la organización";
      toast.error(errorMessage);
    },
  });
};

/**
 * Add a member to the organization
 * Invalidates: organization members
 */
export const useAddMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => addMember(email),
    onSuccess: () => {
      toast.success("Miembro agregado exitosamente");
      // Invalidate members list
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.members(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al agregar el miembro";
      toast.error(errorMessage);
    },
  });
};

/**
 * Delete a member from the organization
 * Invalidates: organization members
 */
export const useDeleteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => deleteMember(memberId),
    onSuccess: () => {
      toast.success("Miembro eliminado exitosamente");
      // Invalidate members list
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.members(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al eliminar el miembro";
      toast.error(errorMessage);
    },
  });
};

/**
 * Update organization credits/quotes
 * Invalidates: organization credits, accounts credits, potentially membership
 */
export const useUpdateOrganizationCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditData) => updateCredits(data),
    onSuccess: () => {
      toast.success("Créditos actualizados exitosamente");
      // Invalidate credits across multiple query keys
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.credits(),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.credits() });
      // May affect membership if credits are related to plan
      queryClient.invalidateQueries({ queryKey: queryKeys.membership.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error || "Error al actualizar los créditos";
      toast.error(errorMessage);
    },
  });
};

/**
 * Invite users to organization
 * Invalidates: organization members (to show pending invitations)
 */
export const useInviteToOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emails: string[]) => inviteToOrganization(emails),
    onSuccess: () => {
      toast.success("Invitaciones enviadas exitosamente");
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.members(),
      });
    },
  });
};

/**
 * Join an organization using an invite token
 * Invalidates: organization data, members, and user's account data
 */
export const useJoinOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteToken: string) => joinToOrganization(inviteToken),
    onSuccess: () => {
      toast.success("Te has unido a la organización exitosamente");
      // Invalidate all organization data as user is now part of org
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al unirse a la organización";
      toast.error(errorMessage);
    },
  });
};
